import { ScaleLib } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './mode-scale-display.module.css'

const NOTES_PER_OCTAVE = 7

export interface ModeScaleDisplayProps {
  /** The scale whose notes to render — modeScale or parallelScale per parent state. */
  scale: Scale
  /**
   * 0-indexed position into the seven scale-degree notes whose pitch should glow.
   * Same index for both modeScale and parallelScale — the alteration is at the same
   * scale-degree position; the pitch is what changes.
   */
  characteristicDegreeIndex: number
  /**
   * Lexicon id used to caption the glowing note. The parent flips this between
   * the mode's own `mode-{X}-signature` id (resting state) and the generic
   * `mode-characteristic-note` id (compare-to-parallel state).
   */
  captionLexiconId: string
}

/**
 * Mode Lab v1 scale display (§4.3 / §6). Horizontal row of seven note labels
 * with the characteristic-degree pitch highlighted and captioned by the
 * level-resolved lexicon tagLabel.
 *
 * §8.4 enharmonic spelling — defers entirely to `ScaleLib.getNotes`, which
 * already handles tonic-relative spelling (F# Lydian renders with F#; G♭
 * Lydian renders with G♭). The component is spelling-agnostic.
 */
export const ModeScaleDisplay: FC<ModeScaleDisplayProps> = props => {
  const { scale, characteristicDegreeIndex, captionLexiconId } = props
  const notes = ScaleLib.getNotes(scale).slice(0, NOTES_PER_OCTAVE)
  const copy = useTheoryCopy(captionLexiconId)
  return (
    <div className={styles.row} role="group" aria-label="Scale notes">
      <div className={styles.notes}>
        {notes.map((note, i) => {
          const isCharacteristic = i === characteristicDegreeIndex
          return (
            <span
              key={i}
              className={isCharacteristic ? styles.noteCharacteristic : styles.note}
              aria-current={isCharacteristic ? 'true' : undefined}
            >
              {note}
            </span>
          )
        })}
      </div>
      {copy ? (
        <p className={styles.caption} data-testid="mode-scale-caption">
          {copy.tag}
        </p>
      ) : null}
    </div>
  )
}
